import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  CheckCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Phone,
  Download,
  Search,
  ChevronDown,
  X
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line
} from 'recharts';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const COLORS = ['#38a169', '#e53e3e', '#805ad5', '#d69e2e', '#3182ce'];

// Componente MultiSelect com checkboxes
const MultiSelect = ({ options, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(v => v !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const clearAll = () => onChange([]);

  const displayText = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => o.id === selected[0])?.name || '1 selecionado')
      : `${selected.length} selecionados`;

  return (
    <div className="multi-select" ref={ref}>
      <div className="multi-select-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span className="multi-select-text">{displayText}</span>
        <ChevronDown size={14} className={`multi-select-arrow ${isOpen ? 'open' : ''}`} />
      </div>
      {isOpen && (
        <div className="multi-select-dropdown">
          {selected.length > 0 && (
            <div className="multi-select-option multi-select-clear" onClick={clearAll}>
              <X size={14} />
              <span>Limpar selecao</span>
            </div>
          )}
          {options.map(option => (
            <div key={option.id} className="multi-select-option" onClick={() => toggleOption(option.id)}>
              <input type="checkbox" checked={selected.includes(option.id)} readOnly />
              <span>{option.name}</span>
            </div>
          ))}
          {options.length === 0 && (
            <div className="multi-select-empty">Nenhuma opcao disponivel</div>
          )}
        </div>
      )}
    </div>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('telefonia');

  // Filtros - Data padrao: ultimos 30 dias
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: new Date().toISOString().split('T')[0],
    fila: [],
    status: [],
    operador: [],
    telefone: ''
  });

  // Dados Dashboard
  const [overview, setOverview] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [byHour, setByHour] = useState([]);
  const [byQueue, setByQueue] = useState([]);
  const [byAgent, setByAgent] = useState([]);
  const [operatorsStatus, setOperatorsStatus] = useState({ total: 0, disponivel: 0, emChamada: 0, emPausa: 0 });

  // Dados Registros
  const [records, setRecords] = useState({ data: [], total: 0 });
  const [recordsPage, setRecordsPage] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const recordsPerPage = 50;

  // Filtros disponiveis
  const [queues, setQueues] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [operators, setOperators] = useState([]);

  // Carregar filtros
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [queuesRes, statusRes, operatorsRes] = await Promise.all([
          axios.get(`${API_URL}/api/telefonia/filters/queues`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/telefonia/filters/status`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/telefonia/filters/operators`).catch(() => ({ data: [] }))
        ]);
        setQueues(queuesRes.data);
        setStatusList(statusRes.data);
        setOperators(operatorsRes.data);
      } catch (err) {
        console.error('Erro ao carregar filtros:', err);
      }
    };
    loadFilters();
  }, []);

  // Carregar dados do dashboard
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.fila.length > 0) params.append('fila', filters.fila.join(','));
      if (filters.status.length > 0) params.append('status', filters.status.join(','));
      if (filters.operador.length > 0) params.append('operador', filters.operador.join(','));

      const queryString = params.toString();

      const [overviewRes, callStatusRes, byHourRes, byQueueRes, byAgentRes, operatorsStatusRes] = await Promise.all([
        axios.get(`${API_URL}/api/telefonia/overview?${queryString}`),
        axios.get(`${API_URL}/api/telefonia/call-status?${queryString}`),
        axios.get(`${API_URL}/api/telefonia/by-hour?${queryString}`),
        axios.get(`${API_URL}/api/telefonia/by-queue?${queryString}`),
        axios.get(`${API_URL}/api/telefonia/by-agent?${queryString}`),
        axios.get(`${API_URL}/api/telefonia/operators-status`).catch(() => ({ data: { total: 0, disponivel: 0, emChamada: 0, emPausa: 0 } }))
      ]);

      setOverview(overviewRes.data);
      setCallStatus(callStatusRes.data);
      setByHour(byHourRes.data);
      setByQueue(byQueueRes.data);
      setByAgent(byAgentRes.data);
      setOperatorsStatus(operatorsStatusRes.data);

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados. Verifique se o backend esta rodando.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, JSON.stringify(filters.fila), JSON.stringify(filters.status), JSON.stringify(filters.operador)]);

  // Carregar registros
  const fetchRecords = useCallback(async () => {
    try {
      setRecordsLoading(true);

      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.fila.length > 0) params.append('fila', filters.fila.join(','));
      if (filters.status.length > 0) params.append('status', filters.status.join(','));
      if (filters.operador.length > 0) params.append('operador', filters.operador.join(','));
      if (filters.telefone) params.append('telefone', filters.telefone);
      params.append('limit', recordsPerPage);
      params.append('offset', recordsPage * recordsPerPage);

      const response = await axios.get(`${API_URL}/api/telefonia/records?${params.toString()}`);
      setRecords(response.data);

    } catch (err) {
      console.error('Erro ao carregar registros:', err);
    } finally {
      setRecordsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), recordsPage]);

  // Carregar dados baseado na tab ativa
  useEffect(() => {
    if (activeTab === 'telefonia') {
      fetchDashboardData();
    } else if (activeTab === 'registros') {
      fetchRecords();
    }
  }, [activeTab, fetchDashboardData, fetchRecords]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setRecordsPage(0);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setRecordsPage(0);
  };

  // Paginacao
  const totalPages = Math.ceil(records.total / recordsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Atendida': return '#38a169';
      case 'Abandono URA': return '#e53e3e';
      case 'Abandono': return '#805ad5';
      default: return '#64748b';
    }
  };

  // Exportar registros para CSV
  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.fila.length > 0) params.append('fila', filters.fila.join(','));
      if (filters.status.length > 0) params.append('status', filters.status.join(','));
      if (filters.operador.length > 0) params.append('operador', filters.operador.join(','));
      if (filters.telefone) params.append('telefone', filters.telefone);
      params.append('limit', 10000);
      params.append('offset', 0);

      const response = await axios.get(`${API_URL}/api/telefonia/records?${params.toString()}`);
      const data = response.data.data;

      if (data.length === 0) {
        alert('Nenhum registro para exportar');
        return;
      }

      const headers = ['Data/Hora', 'Telefone', 'Status', 'Fila', 'Operador', 'T. URA', 'T. Espera', 'T. Conversa', 'T. Total'];
      const csvContent = [
        headers.join(';'),
        ...data.map(row => [
          row.data_hora || '',
          row.telefone_formatado || row.telefone || '',
          row.status || '',
          row.fila || '',
          row.operador || '',
          row.tempo_ura || '00:00:00',
          row.tempo_espera || '00:00:00',
          row.tempo_conversa || '00:00:00',
          row.tempo_total || '00:00:00'
        ].join(';'))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `registros_${filters.startDate}_${filters.endDate}.csv`;
      link.click();
    } catch (err) {
      console.error('Erro ao exportar:', err);
      alert('Erro ao exportar registros');
    }
  };

  if (loading && !overview && activeTab === 'telefonia') {
    return (
      <div className="app">
        <main className="main-content">
          <div className="loading">
            <div className="loading-spinner"></div>
            <span>Carregando dados...</span>
          </div>
        </main>
      </div>
    );
  }

  if (error && !overview && activeTab === 'telefonia') {
    return (
      <div className="app">
        <main className="main-content">
          <div className="error">{error}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'telefonia' ? 'active' : ''}`}
          onClick={() => handleTabChange('telefonia')}
        >
          Telefonia
        </button>
        <button
          className={`tab ${activeTab === 'registros' ? 'active' : ''}`}
          onClick={() => handleTabChange('registros')}
        >
          Registros
        </button>
      </div>

      {/* Filtros */}
      <div className="filters">
        <div className="filter-group">
          <Calendar size={16} className="filter-label" />
          <span className="filter-label">Periodo:</span>
          <input
            type="date"
            className="filter-input"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
          <span>-</span>
          <input
            type="date"
            className="filter-input"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <MultiSelect
            options={queues.map(q => ({ id: q.name, name: q.name }))}
            selected={filters.fila}
            onChange={(val) => handleFilterChange('fila', val)}
            placeholder="Todas as Filas"
          />
        </div>

        <div className="filter-group">
          <MultiSelect
            options={statusList.map(s => ({ id: s.name, name: s.name }))}
            selected={filters.status}
            onChange={(val) => handleFilterChange('status', val)}
            placeholder="Todos os Status"
          />
        </div>

        <div className="filter-group">
          <MultiSelect
            options={operators.map(o => ({ id: o.name, name: o.name }))}
            selected={filters.operador}
            onChange={(val) => handleFilterChange('operador', val)}
            placeholder="Todos os Operadores"
          />
        </div>

      </div>

      <main className="main-content">
        {/* TAB TELEFONIA */}
        {activeTab === 'telefonia' && (
          <>
            {/* Cards Principais - Linha 1 */}
            <div className="cards-grid">
              <div className="card progress-card">
                <div className="card-header">
                  <span className="card-title">NS</span>
                </div>
                <div className="metric-row">
                  <span className="card-value">{overview?.nivelServico || 0}%</span>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${parseFloat(overview?.nivelServico || 0) >= 85 ? 'success' : parseFloat(overview?.nivelServico || 0) >= 70 ? 'warning' : 'danger'}`}
                      style={{ width: `${Math.min(overview?.nivelServico || 0, 100)}%` }}
                    >
                      {parseFloat(overview?.nivelServico || 0) >= 85 && (
                        <span><CheckCircle size={14} /> Meta excedida</span>
                      )}
                    </div>
                  </div>
                  <div className="progress-label">
                    <span>0</span>
                    <span>Meta 85,00%</span>
                  </div>
                </div>
              </div>

              <div className="card progress-card">
                <div className="card-header">
                  <span className="card-title">Abandono Fila</span>
                </div>
                <div className="metric-row">
                  <span className="card-value">{overview?.percentAbandonoFila || 0}%</span>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${parseFloat(overview?.percentAbandonoFila || 0) <= 5 ? 'success' : parseFloat(overview?.percentAbandonoFila || 0) <= 10 ? 'warning' : 'danger'}`}
                      style={{ width: `${Math.min(parseFloat(overview?.percentAbandonoFila || 0) * 10, 100)}%` }}
                    />
                  </div>
                  <div className="progress-label">
                    <span>0</span>
                    <span>Meta 5,00%</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <span className="card-title">TMA</span>
                </div>
                <div className="card-value time">{overview?.tma || '00:00:00'}</div>
                <div className="card-subtitle">Tempo Medio de Atendimento</div>
              </div>

              <div className="card">
                <div className="card-header">
                  <span className="card-title">TME</span>
                </div>
                <div className="card-value time">{overview?.tme || '00:00:00'}</div>
                <div className="card-subtitle">Tempo Medio de Espera</div>
              </div>
            </div>

            {/* Cards Secundarios - Linha 2 */}
            <div className="cards-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Total Recebidas</span>
                </div>
                <div className="card-value">{overview?.recebidas || 0}</div>
                <div className="card-subtitle">Ligacoes recebidas</div>
              </div>

              <div className="card pie-card">
                <div className="card-header">
                  <span className="card-title">Status das Lig.</span>
                </div>
                <div className="pie-container">
                  <div className="pie-legend">
                    {callStatus?.data?.map((item, index) => (
                      <div key={item.status} className="pie-legend-item">
                        <div
                          className="pie-legend-color"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span>{item.status}</span>
                        <span className="pie-legend-value">{item.percentual}%</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ width: 100, height: 100 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={callStatus?.data || []}
                          dataKey="total"
                          nameKey="status"
                          cx="50%"
                          cy="50%"
                          innerRadius={25}
                          outerRadius={45}
                        >
                          {callStatus?.data?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{callStatus?.totalGeral || 0}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>TOTAL</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cards de Operadores */}
            <div className="operators-grid">
              <div className="operator-card">
                <div className="operator-value">{operatorsStatus.total}</div>
                <div className="operator-label">Tot. de operadores</div>
              </div>
              <div className="operator-card">
                <div className="operator-value">{operatorsStatus.disponivel}</div>
                <div className="operator-label">Operadores disp.</div>
              </div>
              <div className="operator-card">
                <div className="operator-value">{operatorsStatus.emChamada}</div>
                <div className="operator-label">Operadores em cham.</div>
              </div>
              <div className="operator-card">
                <div className="operator-value">{operatorsStatus.emPausa}</div>
                <div className="operator-label">Operadores pausados</div>
              </div>
            </div>

            {/* Grafico de Entrantes por Hora */}
            <div className="charts-section">
              <div className="chart-card">
                <h3>Entrantes por Hora</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={byHour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="hora" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="atendidas" name="Atendida" fill="#3182ce" />
                    <Bar dataKey="abandonoUra" name="Abandono URA" fill="#e53e3e" />
                    <Bar dataKey="abandonoFila" name="Abandono" fill="#805ad5" />
                    <Line type="monotone" dataKey="nivelServico" name="NS" stroke="#38a169" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tabela Atendimentos por Hora */}
            <div className="chart-card">
              <h3>Atendimentos por Hora</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Hora</th>
                      <th>Recebidas</th>
                      <th>Atendidas</th>
                      <th>Nivel de servico %</th>
                      <th>%TX Atd.</th>
                      <th>TT Aband. Fila</th>
                      <th>Abandono URA</th>
                      <th>% Ab URA</th>
                      <th>TMA</th>
                      <th>TME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byHour.map((row, index) => (
                      <tr key={index}>
                        <td>{row.hora}</td>
                        <td>{row.recebidas}</td>
                        <td>{row.atendidas}</td>
                        <td>{row.nivelServico}%</td>
                        <td>{row.taxaAtendimento}%</td>
                        <td>{row.abandonoFila}</td>
                        <td>{row.abandonoUra}</td>
                        <td>{((row.abandonoUra / (row.recebidas || 1)) * 100).toFixed(2)}%</td>
                        <td>{row.tma}</td>
                        <td>{row.tme}</td>
                      </tr>
                    ))}
                    {byHour.length > 0 && (
                      <tr className="total-row">
                        <td>TOTAL</td>
                        <td>{overview?.recebidas}</td>
                        <td>{overview?.atendidas}</td>
                        <td>{overview?.nivelServico}%</td>
                        <td>{overview?.taxaAtendimento}%</td>
                        <td>{overview?.abandonoFila}</td>
                        <td>{overview?.abandonoUra}</td>
                        <td>{overview?.percentAbandonoUra}%</td>
                        <td>{overview?.tma}</td>
                        <td>{overview?.tme}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tabela Fila de Atendimento */}
            <div className="chart-card" style={{ marginTop: '1.5rem' }}>
              <h3>Fila de Atendimento</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fila</th>
                      <th>Recebidas</th>
                      <th>Atendidas</th>
                      <th>Nivel de servico %</th>
                      <th>%TX Atd.</th>
                      <th>TT Aband. Fila</th>
                      <th>Abandono URA</th>
                      <th>% Ab URA</th>
                      <th>TMA</th>
                      <th>TME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byQueue.map((row, index) => (
                      <tr key={index}>
                        <td>{row.fila}</td>
                        <td>{row.recebidas}</td>
                        <td>{row.atendidas}</td>
                        <td>{row.nivelServico}%</td>
                        <td>{row.taxaAtendimento}%</td>
                        <td>{row.abandonoFila}</td>
                        <td>{row.abandonoUra}</td>
                        <td>{row.percentAbandonoUra}%</td>
                        <td>{row.tma}</td>
                        <td>{row.tme}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tabela Agentes */}
            <div className="chart-card" style={{ marginTop: '1.5rem' }}>
              <h3>Agentes</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Operador</th>
                      <th>Recebidas</th>
                      <th>Atendidas</th>
                      <th>Nivel de servico %</th>
                      <th>%TX Atd.</th>
                      <th>TT Aband. Fila</th>
                      <th>Abandono URA</th>
                      <th>% Ab URA</th>
                      <th>TMA</th>
                      <th>TME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byAgent.map((row, index) => (
                      <tr key={index}>
                        <td>{row.operador}</td>
                        <td>{row.recebidas}</td>
                        <td>{row.atendidas}</td>
                        <td>{row.nivelServico}%</td>
                        <td>{row.taxaAtendimento}%</td>
                        <td>{row.abandonoFila}</td>
                        <td>{row.abandonoUra}</td>
                        <td>{row.percentAbandonoUra}%</td>
                        <td>{row.tma}</td>
                        <td>{row.tme}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Grafico Atendimentos por Agente */}
            <div className="chart-card" style={{ marginTop: '1.5rem' }}>
              <h3>Atendimentos por Agente</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={byAgent.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="operador" type="category" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="atendidas" name="Atendidas" fill="#3182ce" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* TAB REGISTROS */}
        {activeTab === 'registros' && (
          <div className="chart-card">
            <div className="records-header">
              <h3>
                <Phone size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Registros de Ligacoes
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="records-count">{records.total} registros</span>
                <button
                  onClick={exportToCSV}
                  className="export-btn"
                  title="Exportar para CSV"
                >
                  <Download size={16} />
                  Exportar
                </button>
              </div>
            </div>

            {/* Filtros da Tab Registros */}
            <div className="records-filters">
              <div className="filter-group">
                <Search size={16} style={{ color: '#64748b' }} />
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Buscar por telefone..."
                  value={filters.telefone}
                  onChange={(e) => handleFilterChange('telefone', e.target.value)}
                  style={{ minWidth: '180px' }}
                />
              </div>
              <div className="filter-group">
                <MultiSelect
                  options={statusList.map(s => ({ id: s.name, name: s.name }))}
                  selected={filters.status}
                  onChange={(val) => handleFilterChange('status', val)}
                  placeholder="Todos os Status"
                />
              </div>
              <div className="filter-group">
                <MultiSelect
                  options={operators.map(o => ({ id: o.name, name: o.name }))}
                  selected={filters.operador}
                  onChange={(val) => handleFilterChange('operador', val)}
                  placeholder="Todos os Operadores"
                />
              </div>
              <button
                onClick={() => { setRecordsPage(0); fetchRecords(); }}
                className="search-btn"
              >
                <Search size={16} />
                Buscar
              </button>
            </div>

            {recordsLoading ? (
              <div className="loading" style={{ minHeight: '200px' }}>
                <div className="loading-spinner"></div>
                <span>Carregando registros...</span>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table records-table">
                    <thead>
                      <tr>
                        <th>Data/Hora</th>
                        <th>Telefone</th>
                        <th>Status</th>
                        <th>Fila</th>
                        <th>Operador</th>
                        <th>T. URA</th>
                        <th>T. Espera</th>
                        <th>T. Conversa</th>
                        <th>T. Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.data.map((record) => (
                        <tr key={record.id}>
                          <td>{record.data_hora}</td>
                          <td>{record.telefone_formatado || record.telefone}</td>
                          <td>
                            <span
                              className="status-badge"
                              style={{ backgroundColor: getStatusColor(record.status), color: 'white' }}
                            >
                              {record.status}
                            </span>
                          </td>
                          <td>{record.fila || '-'}</td>
                          <td>{record.operador || '-'}</td>
                          <td>{record.tempo_ura || '00:00:00'}</td>
                          <td>{record.tempo_espera || '00:00:00'}</td>
                          <td>{record.tempo_conversa || '00:00:00'}</td>
                          <td>{record.tempo_total || '00:00:00'}</td>
                        </tr>
                      ))}
                      {records.data.length === 0 && (
                        <tr>
                          <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            Nenhum registro encontrado
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Paginacao */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => setRecordsPage(p => Math.max(0, p - 1))}
                      disabled={recordsPage === 0}
                    >
                      <ChevronLeft size={18} />
                      Anterior
                    </button>
                    <span className="pagination-info">
                      Pagina {recordsPage + 1} de {totalPages}
                    </span>
                    <button
                      className="pagination-btn"
                      onClick={() => setRecordsPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={recordsPage >= totalPages - 1}
                    >
                      Proxima
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
